import { useState, useRef, useEffect, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Material } from '@/features/materials/types/material.types';
import { ChevronsUpDown, Check, Search } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

interface MaterialComboboxProps {
  materials: Material[];
  value: string;
  /**
   * Callback al seleccionar. `priceOverride` se pasa cuando el usuario clickea un
   * chip de precio específico (ej. "Precio mayorista") en lugar del default.
   */
  onSelect: (materialId: string, priceOverride?: number) => void;
  className?: string;
  /** IDs de materiales que se muestran primero con un separador ("De cotización origen"). */
  priorityMaterialIds?: string[];
  /**
   * Ofrecer el costo nacionalizado de Comercia («Usar estos costos en Cotiza»). Lo
   * prende sólo un modelo que parte de un costo y le pone margen: uno de importación
   * (con VALOR_FOB) volvería a sumarle flete y aranceles.
   */
  conCostoNacionalizado?: boolean;
}

export function MaterialCombobox({
  materials,
  value,
  onSelect,
  className,
  priorityMaterialIds,
  conCostoNacionalizado = false,
}: MaterialComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedMaterial = materials.find((m) => m.id === value);

  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return materials;
    const q = search.toLowerCase();
    return materials.filter(
      (m) =>
        m.code.toLowerCase().includes(q) ||
        m.name.toLowerCase().includes(q) ||
        m.externalCode?.toLowerCase().includes(q) ||
        m.lob?.name?.toLowerCase().includes(q)
    );
  }, [materials, search]);

  // Separa priorizados vs resto (respetando el orden del array de prioridad)
  const { priorityList, restList } = useMemo(() => {
    if (!priorityMaterialIds || priorityMaterialIds.length === 0) {
      return { priorityList: [] as Material[], restList: filtered };
    }
    const prioritySet = new Set(priorityMaterialIds);
    const priorityMap = new Map<string, Material>();
    const rest: Material[] = [];
    for (const m of filtered) {
      if (prioritySet.has(m.id)) priorityMap.set(m.id, m);
      else rest.push(m);
    }
    const ordered = priorityMaterialIds
      .map((id) => priorityMap.get(id))
      .filter((m): m is Material => !!m);
    return { priorityList: ordered, restList: rest };
  }, [filtered, priorityMaterialIds]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('h-8 justify-between text-xs font-normal w-full', className)}
        >
          <span className="truncate">
            {selectedMaterial
              ? `${selectedMaterial.code} - ${selectedMaterial.name}`
              : 'Seleccionar material'}
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código, nombre o LOB..."
            className="flex h-7 w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div className="max-h-[260px] overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">
              Sin resultados para "{search}"
            </p>
          ) : (
            <>
              {priorityList.length > 0 && (
                <>
                  <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-primary bg-info-suave rounded-sm mb-1">
                    De cotización origen
                  </div>
                  {priorityList.map((mat) => (
                    <MaterialOption
                      key={`p-${mat.id}`}
                      mat={mat}
                      conCostoNacionalizado={conCostoNacionalizado}
                      isSelected={value === mat.id}
                      onSelect={(priceOverride) => {
                        onSelect(mat.id, priceOverride);
                        setOpen(false);
                      }}
                      highlight
                    />
                  ))}
                  {restList.length > 0 && (
                    <div className="px-2 py-1 mt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground border-t">
                      Otros materiales
                    </div>
                  )}
                </>
              )}
              {restList.map((mat) => (
                <MaterialOption
                  key={mat.id}
                  mat={mat}
                  conCostoNacionalizado={conCostoNacionalizado}
                  isSelected={value === mat.id}
                  onSelect={(priceOverride) => {
                    onSelect(mat.id, priceOverride);
                    setOpen(false);
                  }}
                />
              ))}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MaterialOption({
  mat,
  isSelected,
  onSelect,
  highlight,
  conCostoNacionalizado,
}: {
  mat: Material;
  isSelected: boolean;
  /** `priceOverride` se pasa cuando el usuario clickea un chip de precio específico. */
  onSelect: (priceOverride?: number) => void;
  highlight?: boolean;
  conCostoNacionalizado?: boolean;
}) {
  const entries = [...(mat.priceListEntries ?? [])].sort((a, b) => {
    const ad = a.priceList?.isDefault ? 0 : 1;
    const bd = b.priceList?.isDefault ? 0 : 1;
    if (ad !== bd) return ad - bd;
    return (a.priceList?.name ?? '').localeCompare(b.priceList?.name ?? '');
  });
  const hasPrices = entries.length > 0;
  const nacionalizado = conCostoNacionalizado ? (mat.lastLandedCost ?? null) : null;

  return (
    <div
      className={cn(
        'relative flex w-full select-none items-start rounded-sm px-2 py-1.5 text-xs hover:bg-accent/50',
        isSelected && 'bg-accent',
        highlight && 'border-l-2 border-info-borde'
      )}
    >
      <button
        type="button"
        className="flex flex-1 min-w-0 items-start text-left cursor-pointer"
        onClick={() => onSelect()}
        title={
          hasPrices
            ? 'Seleccionar con precio por defecto'
            : nacionalizado != null
              ? 'Seleccionar con su costo nacionalizado de Comercia'
              : 'Seleccionar (sin precio)'
        }
      >
        <Check
          className={cn('mr-2 mt-0.5 h-3 w-3 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-mono font-semibold">{mat.code}</span>
            {mat.lob && (
              <span className="text-[10px] text-muted-foreground bg-muted px-1 rounded">
                {mat.lob.name}
              </span>
            )}
          </div>
          <p className="text-muted-foreground truncate">{mat.name}</p>
        </div>
      </button>
      {(hasPrices || nacionalizado != null) && (
        <div className="flex flex-wrap gap-1 ml-2 shrink-0 max-w-[55%] justify-end">
          {nacionalizado != null && (
            <button
              type="button"
              onClick={(ev) => {
                ev.stopPropagation();
                onSelect(nacionalizado);
              }}
              title="Costo por unidad puesto en tu almacén, de Comercia: ya trae flete, seguro y tributos. Sólo ponle el margen."
              className="flex flex-col items-end px-1.5 py-0.5 rounded border border-primary/40 bg-primary/10 text-primary text-[10px] leading-tight cursor-pointer hover:opacity-80"
            >
              <span className="font-mono font-bold">
                USD{' '}
                {nacionalizado.toLocaleString('es-BO', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <span className="text-[9px] opacity-85">Nacionalizado · Comercia</span>
            </button>
          )}
          {entries.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={(ev) => {
                ev.stopPropagation();
                onSelect(e.price);
              }}
              title={`Aplicar precio · ${e.priceList?.name ?? '—'}`}
              className={cn(
                'flex flex-col items-end px-1.5 py-0.5 rounded border text-[10px] leading-tight cursor-pointer hover:opacity-80',
                e.priceList?.isDefault
                  ? 'border-info bg-info-suave text-info'
                  : 'border-border bg-white text-foreground'
              )}
            >
              <span className="font-mono font-bold">
                $
                {e.price.toLocaleString('es-BO', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <span className="text-[9px] opacity-85">
                {e.priceList?.name ?? '—'}
                {e.priceList?.isDefault ? ' ★' : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default MaterialCombobox;
